<template>
  <form
    class="kb-chat-input"
    @submit.prevent="handleSend"
  >
    <div class="kb-chat-field">
      <span
        class="kb-chat-prompt"
        aria-hidden="true"
      >›</span>
      <textarea
        ref="inputRef"
        v-model="inputText"
        placeholder="Ask a question"
        aria-label="Ask a question"
        aria-describedby="kb-chat-hint"
        rows="1"
        :disabled="isLoading"
        @keydown.enter.exact.prevent="handleSend"
        @input="autoResize"
      />
      <Button
        type="submit"
        icon="pi pi-send"
        size="small"
        :loading="isLoading"
        :disabled="!inputText.trim()"
        aria-label="Send message"
      />
    </div>
    <p
      id="kb-chat-hint"
      class="kb-chat-hint"
    >
      Enter sends · Shift+Enter for a new line
    </p>
  </form>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import Button from 'primevue/button'

const { sendMessage, isLoading } = useChatbot()

const inputText = ref('')
const inputRef = ref<HTMLTextAreaElement | null>(null)

async function handleSend() {
  const text = inputText.value.trim()
  if (!text || isLoading.value) return

  inputText.value = ''
  resetHeight()
  await sendMessage(text)
}

function autoResize() {
  if (!inputRef.value) return
  inputRef.value.style.height = 'auto'
  const maxHeight = 100
  inputRef.value.style.height = `${Math.min(inputRef.value.scrollHeight, maxHeight)}px`
}

function resetHeight() {
  if (!inputRef.value) return
  inputRef.value.style.height = 'auto'
}

// Ready to type when the panel opens; not on a phone, where focusing
// would throw the keyboard over the sheet
onMounted(() => {
  if (window.matchMedia('(hover: hover)').matches) inputRef.value?.focus()
})
</script>

<style scoped>
.kb-chat-input {
  flex-shrink: 0;
  /* Clear of the home indicator when it's the bottom sheet on a phone */
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--kb-line);
  background: var(--kb-pane);
}

/* A prompt line: the caret glyph, the text, the send button */
.kb-chat-field {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.375rem 0.375rem 0.375rem 0.625rem;
  border: 1px solid var(--kb-line-2);
  border-radius: 3px;
  background: var(--kb-bg);
}

.kb-chat-field:focus-within {
  border-color: var(--kb-accent);
}

.kb-chat-prompt {
  padding-bottom: 0.375rem;
  font: 600 0.9375rem var(--kb-font-mono);
  color: var(--kb-accent);
}

.kb-chat-field textarea {
  flex: 1;
  min-width: 0;
  max-height: 100px;
  padding: 0.3125rem 0;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--kb-fg);
  resize: none;
}

.kb-chat-field textarea::placeholder {
  color: var(--kb-dim);
}

.kb-chat-field textarea:disabled {
  opacity: 0.6;
}

.kb-chat-hint {
  margin: 0.375rem 0 0;
  font: 11px var(--kb-font-mono);
  color: var(--kb-dim);
  text-align: center;
}

/* No zoom on iOS: text fields are 16px on touch screens */
@media (hover: none) and (pointer: coarse) {
  .kb-chat-field textarea {
    font-size: 16px;
  }
}
</style>
